import { useMemo, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import {
  useFailedIngestionsList,
  useIngestionErrorSummary,
} from '@/shared/hooks/api/ingestionDiagnostics'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const WINDOW_OPTIONS = [7, 30, 90] as const

export function IngestionErrorsPanel() {
  const [windowDays, setWindowDays] = useState<(typeof WINDOW_OPTIONS)[number]>(30)
  const [errorCodeFilter, setErrorCodeFilter] = useState<string | null>(null)
  const { data: session, isFetched: isSessionFetched } = useGetUserSession()

  const summaryQuery = useIngestionErrorSummary(windowDays, {
    enabled: isSessionFetched && Boolean(session?.isArtemAdmin),
  })
  const failedQuery = useFailedIngestionsList(
    {
      windowDays,
      errorCode: errorCodeFilter ?? undefined,
    },
    { enabled: isSessionFetched && Boolean(session?.isArtemAdmin) }
  )

  const summary = summaryQuery.data
  const failedItems = failedQuery.data?.pages.flatMap((page) => page.items) ?? []

  const mostCommonError = useMemo(() => {
    if (!summary?.byErrorCode.length) {
      return null
    }
    return summary.byErrorCode[0]
  }, [summary?.byErrorCode])

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex gap-2'>
        {WINDOW_OPTIONS.map((days) => (
          <Button
            key={days}
            size='sm'
            variant={windowDays === days ? 'default' : 'outline'}
            onClick={() => setWindowDays(days)}
          >
            {days}d
          </Button>
        ))}
      </div>

      {summary && (
        <>
          <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-5'>
            <div className='rounded-lg border bg-card p-4'>
              <div className='text-xs uppercase text-muted-foreground'>
                Total failures
              </div>
              <div className='text-2xl font-semibold'>{summary.totalFailures}</div>
            </div>
            <div className='rounded-lg border bg-card p-4'>
              <div className='text-xs uppercase text-muted-foreground'>
                Most common
              </div>
              <div className='font-mono text-sm'>
                {mostCommonError?.errorCode ?? '—'}
              </div>
              <div className='text-xs text-muted-foreground'>
                {mostCommonError ? `${mostCommonError.count} failures` : ''}
              </div>
            </div>
            <div className='rounded-lg border bg-card p-4'>
              <div className='text-xs uppercase text-muted-foreground'>
                Stuck in-flight
              </div>
              <div className='text-2xl font-semibold'>
                {summary.needsAttention.stuck}
              </div>
              <Link
                className='text-xs text-foreground underline decoration-dotted underline-offset-2'
                to='/admin/diagnostics/ingestions?tab=ingestions&attention=stuck'
              >
                View list
              </Link>
            </div>
            <div className='rounded-lg border bg-card p-4'>
              <div className='text-xs uppercase text-muted-foreground'>
                No RAG job
              </div>
              <div className='text-2xl font-semibold'>
                {summary.needsAttention.missingRagJobId}
              </div>
              <Link
                className='text-xs text-foreground underline decoration-dotted underline-offset-2'
                to='/admin/diagnostics/ingestions?tab=ingestions&attention=missingRagJobId'
              >
                View list
              </Link>
            </div>
            <div className='rounded-lg border bg-card p-4'>
              <div className='text-xs uppercase text-muted-foreground'>
                Pages↔chunks gap
              </div>
              <div className='text-2xl font-semibold'>
                {summary.needsAttention.pagesChunksMismatch}
              </div>
              <Link
                className='text-xs text-foreground underline decoration-dotted underline-offset-2'
                to='/admin/diagnostics/ingestions?tab=ingestions&attention=pagesChunksMismatch'
              >
                View list
              </Link>
            </div>
          </div>

          <div className='grid gap-4 lg:grid-cols-2'>
            <div className='rounded-lg border bg-card p-4'>
              <h2 className='mb-3 text-sm font-semibold'>
                Failures by error code
              </h2>
              <div className='h-64'>
                <ResponsiveContainer width='100%' height='100%'>
                  <BarChart data={summary.byErrorCode.slice(0, 12)}>
                    <CartesianGrid strokeDasharray='3 3' vertical={false} />
                    <XAxis dataKey='errorCode' hide />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar
                      dataKey='count'
                      fill='var(--primary)'
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {summary.byErrorCode.length > 0 && (
                <div className='mt-2 flex flex-wrap gap-1'>
                  {summary.byErrorCode.slice(0, 8).map((row) => (
                    <Button
                      key={row.errorCode}
                      size='sm'
                      variant={
                        errorCodeFilter === row.errorCode ? 'default' : 'outline'
                      }
                      className='h-7 font-mono text-[10px]'
                      onClick={() =>
                        setErrorCodeFilter((current) =>
                          current === row.errorCode ? null : row.errorCode
                        )
                      }
                    >
                      {row.errorCode} ({row.count})
                    </Button>
                  ))}
                </div>
              )}
            </div>

            <div className='rounded-lg border bg-card p-4'>
              <h2 className='mb-3 text-sm font-semibold'>Failure trend</h2>
              <div className='h-64'>
                <ResponsiveContainer width='100%' height='100%'>
                  <LineChart data={summary.trend}>
                    <CartesianGrid strokeDasharray='3 3' vertical={false} />
                    <XAxis dataKey='day' hide />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Line
                      type='monotone'
                      dataKey='failures'
                      stroke='var(--destructive)'
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {summary.byOrganization.length > 0 && (
            <div className='rounded-lg border bg-card p-4'>
              <h2 className='mb-3 text-sm font-semibold'>
                Failure rate by organization
              </h2>
              <div className='rounded-md border'>
                <Table className='table-fixed'>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Organization</TableHead>
                      <TableHead className='w-[6rem]'>Failures</TableHead>
                      <TableHead className='w-[7rem]'>Failure rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary.byOrganization.slice(0, 15).map((row) => (
                      <TableRow key={row.organizationId}>
                        <TableCell
                          className='max-w-0 truncate'
                          title={row.organizationName}
                        >
                          {row.organizationName}
                        </TableCell>
                        <TableCell>{row.failures}</TableCell>
                        <TableCell>
                          {(row.failureRate * 100).toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </>
      )}

      <section className='space-y-3'>
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <h2 className='text-lg font-semibold'>Failed ingestions</h2>
          {errorCodeFilter && (
            <Button
              size='sm'
              variant='outline'
              onClick={() => setErrorCodeFilter(null)}
            >
              Clear filter: {errorCodeFilter}
            </Button>
          )}
        </div>
        <div className='rounded-lg border'>
          <Table className='table-fixed'>
            <TableHeader>
              <TableRow>
                <TableHead className='w-[9rem]'>Failed at</TableHead>
                <TableHead className='w-[10rem]'>Organization</TableHead>
                <TableHead className='w-[12rem]'>Source</TableHead>
                <TableHead className='w-[8rem]'>Error code</TableHead>
                <TableHead>Message</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {failedItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className='whitespace-nowrap text-xs text-muted-foreground'>
                    {item.failedAt
                      ? new Date(item.failedAt).toLocaleString()
                      : '—'}
                  </TableCell>
                  <TableCell
                    className='max-w-0 truncate'
                    title={item.organization.name}
                  >
                    {item.organization.name}
                  </TableCell>
                  <TableCell className='max-w-0 truncate'>
                    <Link
                      className='text-foreground underline decoration-dotted underline-offset-2'
                      to={`/admin/diagnostics/ingestions/${item.id}`}
                      title={item.name}
                    >
                      {item.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant='destructive'
                      className='max-w-full truncate font-mono text-[10px]'
                      title={item.ingestionErrorCode ?? 'UNCODED'}
                    >
                      {item.ingestionErrorCode ?? 'UNCODED'}
                    </Badge>
                  </TableCell>
                  <TableCell
                    className='max-w-0 truncate text-xs text-muted-foreground'
                    title={item.ingestionError ?? undefined}
                  >
                    {item.ingestionError ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
              {failedItems.length === 0 && !failedQuery.isLoading && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className='py-8 text-center text-muted-foreground'
                  >
                    No failures in this window.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {failedQuery.hasNextPage && (
          <Button
            variant='outline'
            onClick={() => failedQuery.fetchNextPage()}
            disabled={failedQuery.isFetchingNextPage}
          >
            Load more
          </Button>
        )}
      </section>
    </div>
  )
}

export default function IngestionErrorsPage() {
  return (
    <Navigate to='/admin/diagnostics/ingestions?tab=errors' replace />
  )
}
